import * as Sentry from '@sentry/react';
/**
 * API service for Marketing module operations
 * Handles CRUD operations for counterparties, contracts, media spaces, promotional areas, and bookings
 */

/**
 * Safely encode an identifier for use in a URL path segment.
 * Throws if the value is clearly invalid to avoid sending dangerous paths.
 */
const encodePathId = (id, name = 'id') => {
    if (id === null || id === undefined) {
        throw new Error(`Missing required ${name}`);
    }

    const str = String(id);

    // Basic sanity check: allow common ID characters only
    // (alphanumerics, dash, and underscore). Adjust as needed.
    if (!/^[A-Za-z0-9_-]+$/.test(str)) {
        throw new Error(`Invalid ${name} format`);
    }

    return encodeURIComponent(str);
};

/**
 * Helper function to handle API responses with improved error messages.
 * Provides more specific error details from the API when available.
 */
const handleApiResponse = async (response, defaultErrorMessage, { returnFullData = false, defaultValue } = {}) => {
    let data;
    try {
        data = await response.json();
    } catch (catchError) {
        Sentry.captureException(catchError);
        const error = new Error(defaultErrorMessage || 'Request failed');
        error.status = response.status;
        error.statusText = response.statusText;
        throw error;
    }

    if (data && data.success) {
        if (returnFullData) {
            return data;
        }
        if (Object.prototype.hasOwnProperty.call(data, 'data')) {
            if (data.data == null && defaultValue !== undefined) {
                return defaultValue;
            }
            return data.data;
        }
        return data;
    }

    const message =
        (data && (data.message || data.error)) ||
        (typeof data === 'string' && data) ||
        defaultErrorMessage ||
        'Request failed';

    const error = new Error(message);
    error.status = response.status;
    error.statusText = response.statusText;
    error.responseData = data;
    throw error;
};

// ============================================
// COUNTERPARTIES
// ============================================

export const getAllCounterparties = async (apiFetch) => {
    const response = await apiFetch('/api/Marketing/Counterparties');
    return handleApiResponse(response, 'Failed to fetch counterparties', { defaultValue: [] });
};

export const getCounterparty = async (apiFetch, id) => {
    const response = await apiFetch(`/api/Marketing/Counterparties/${id}`);
    return handleApiResponse(response, 'Failed to fetch counterparty');
};

export const createCounterparty = async (apiFetch, counterpartyData) => {
    const response = await apiFetch('/api/Marketing/Counterparties', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(counterpartyData)
    });
    return handleApiResponse(response, 'Failed to create counterparty');
};

export const updateCounterparty = async (apiFetch, id, counterpartyData) => {
    const response = await apiFetch(`/api/Marketing/Counterparties/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(counterpartyData)
    });
    return handleApiResponse(response, 'Failed to update counterparty');
};

export const deleteCounterparty = async (apiFetch, id) => {
    const response = await apiFetch(`/api/Marketing/Counterparties/${id}`, {
        method: 'DELETE'
    });
    return handleApiResponse(response, 'Failed to delete counterparty', { returnFullData: true });
};

// ============================================
// CONTRACTS
// ============================================

export const getAllContracts = async (apiFetch) => {
    const response = await apiFetch('/api/Marketing/Contracts');
    return handleApiResponse(response, 'Failed to fetch contracts', { defaultValue: [] });
};

export const getContract = async (apiFetch, id) => {
    const response = await apiFetch(`/api/Marketing/Contracts/${id}`);
    return handleApiResponse(response, 'Failed to fetch contract');
};

export const createContract = async (apiFetch, contractData) => {
    const response = await apiFetch('/api/Marketing/Contracts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contractData)
    });
    return handleApiResponse(response, 'Failed to create contract');
};

export const updateContract = async (apiFetch, id, contractData) => {
    const response = await apiFetch(`/api/Marketing/Contracts/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contractData)
    });
    return handleApiResponse(response, 'Failed to update contract');
};

export const validateContract = async (apiFetch, counterpartyId, propertyId, startDate, endDate) => {
    const params = new URLSearchParams({
        counterpartyId,
        propertyId,
        startDate,
        endDate
    });
    const response = await apiFetch(`/api/Marketing/Contracts/validate?${params}`);
    return handleApiResponse(response, 'Failed to validate contract');
};

export const getContractsByCounterparty = async (apiFetch, counterpartyId) => {
    const response = await apiFetch(`/api/Marketing/Contracts/counterparty/${counterpartyId}`);
    return handleApiResponse(response, 'Failed to fetch contracts for counterparty', { defaultValue: [] });
};

export const getContractsByProperty = async (apiFetch, propertyId) => {
    const response = await apiFetch(`/api/Marketing/Contracts/property/${propertyId}`);
    return handleApiResponse(response, 'Failed to fetch contracts for property', { defaultValue: [] });
};

// ============================================
// CONTRACT DOCUMENTS
// ============================================

export const uploadContractDocument = async (apiFetch, contractId, file) => {
    // Validate contractId to prevent path traversal/injection
    const safeContractId = encodePathId(contractId, 'contractId');

    // Basic client-side validation of the uploaded file
    if (!file) {
        throw new Error('No file provided for upload');
    }

    // Ensure we have a File/Blob-like object with expected properties
    const { name, size, type } = file;

    if (typeof size !== 'number' || size <= 0) {
        throw new Error('Invalid file size');
    }

    // Limit file size (e.g. 10 MB) to reduce potential abuse and improve UX
    const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
    if (size > MAX_FILE_SIZE_BYTES) {
        throw new Error('File is too large to upload');
    }

    // Allow only a safe subset of document types commonly used for contracts
    // Note: Client-side MIME type validation can be spoofed. Server-side validation
    // should verify actual file content (magic bytes) for security.
    const allowedMimeTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'image/jpeg',
        'image/png'
    ];

    if (type && !allowedMimeTypes.includes(type)) {
        throw new Error('Unsupported file type');
    }

    // Filename sanitization: trim, remove control characters, path separators, and limit length
    let safeName = typeof name === 'string' ? name.trim() : '';
    if (safeName) {
        // Remove control characters, path separators, and other dangerous characters
        // eslint-disable-next-line no-control-regex
        safeName = safeName.replace(/[\0-\x1F\x7F/\\:*?"<>|]+/g, '');
        // Limit filename length to prevent issues
        const maxLength = 255;
        if (safeName.length > maxLength) {
            // Preserve extension if possible
            const lastDot = safeName.lastIndexOf('.');
            if (lastDot > 0 && lastDot > maxLength - 20) {
                const ext = safeName.substring(lastDot);
                safeName = safeName.substring(0, maxLength - ext.length) + ext;
            } else {
                safeName = safeName.substring(0, maxLength);
            }
        }
        // Ensure filename is not empty after sanitization and has valid extension
        if (!safeName || safeName === '.' || !safeName.includes('.')) {
            throw new Error('Invalid filename after sanitization');
        }
    }

    const formData = new FormData();
    if (safeName) {
        formData.append('file', file, safeName);
    } else {
        formData.append('file', file);
    }

    const response = await apiFetch(`/api/Marketing/Contracts/${safeContractId}/upload`, {
        method: 'POST',
        body: formData
    });
    return handleApiResponse(response, 'Failed to upload document');
};

export const getContractDocuments = async (apiFetch, contractId) => {
    const safeContractId = encodePathId(contractId, 'contractId');
    const response = await apiFetch(`/api/Marketing/Contracts/${safeContractId}/documents`);
    return handleApiResponse(response, 'Failed to fetch documents', { defaultValue: [] });
};

export const deleteContractDocument = async (apiFetch, contractId, fileId) => {
    const safeContractId = encodePathId(contractId, 'contractId');
    const safeFileId = encodePathId(fileId, 'fileId');
    const response = await apiFetch(`/api/Marketing/Contracts/${safeContractId}/documents/${safeFileId}`, {
        method: 'DELETE'
    });
    return handleApiResponse(response, 'Failed to delete document', { returnFullData: true });
};

// ============================================
// MEDIA SPACES
// ============================================

export const getAllMediaSpaces = async (apiFetch) => {
    const response = await apiFetch('/api/Marketing/MediaSpaces');
    return handleApiResponse(response, 'Failed to fetch media spaces', { defaultValue: [] });
};

export const getMediaSpace = async (apiFetch, id) => {
    const response = await apiFetch(`/api/Marketing/MediaSpaces/${id}`);
    return handleApiResponse(response, 'Failed to fetch media space');
};

export const createMediaSpace = async (apiFetch, spaceData) => {
    const response = await apiFetch('/api/Marketing/MediaSpaces', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(spaceData)
    });
    return handleApiResponse(response, 'Failed to create media space');
};

export const updateMediaSpace = async (apiFetch, id, spaceData) => {
    const response = await apiFetch(`/api/Marketing/MediaSpaces/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(spaceData)
    });
    return handleApiResponse(response, 'Failed to update media space');
};

export const deleteMediaSpace = async (apiFetch, id) => {
    const response = await apiFetch(`/api/Marketing/MediaSpaces/${id}`, {
        method: 'DELETE'
    });
    return handleApiResponse(response, 'Failed to delete media space', { returnFullData: true });
};

export const getMediaSpacesByProperty = async (apiFetch, propertyId) => {
    const response = await apiFetch(`/api/Marketing/MediaSpaces/property/${propertyId}`);
    return handleApiResponse(response, 'Failed to fetch media spaces for property', { defaultValue: [] });
};

// ============================================
// PROMOTIONAL AREAS
// ============================================

export const getAllPromotionalAreas = async (apiFetch) => {
    const response = await apiFetch('/api/Marketing/PromotionalAreas');
    return handleApiResponse(response, 'Failed to fetch promotional areas', { defaultValue: [] });
};

export const getPromotionalArea = async (apiFetch, id) => {
    const response = await apiFetch(`/api/Marketing/PromotionalAreas/${id}`);
    return handleApiResponse(response, 'Failed to fetch promotional area');
};

export const createPromotionalArea = async (apiFetch, areaData) => {
    const response = await apiFetch('/api/Marketing/PromotionalAreas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(areaData)
    });
    return handleApiResponse(response, 'Failed to create promotional area');
};

export const updatePromotionalArea = async (apiFetch, id, areaData) => {
    const response = await apiFetch(`/api/Marketing/PromotionalAreas/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(areaData)
    });
    return handleApiResponse(response, 'Failed to update promotional area');
};

export const deletePromotionalArea = async (apiFetch, id) => {
    const response = await apiFetch(`/api/Marketing/PromotionalAreas/${id}`, {
        method: 'DELETE'
    });
    return handleApiResponse(response, 'Failed to delete promotional area', { returnFullData: true });
};

export const getPromotionalAreasByProperty = async (apiFetch, propertyId) => {
    const response = await apiFetch(`/api/Marketing/PromotionalAreas/property/${propertyId}`);
    return handleApiResponse(response, 'Failed to fetch promotional areas for property', { defaultValue: [] });
};

// ============================================
// MEDIA BOOKINGS
// ============================================

export const getAllMediaBookings = async (apiFetch) => {
    const response = await apiFetch('/api/Marketing/MediaBookings');
    return handleApiResponse(response, 'Failed to fetch media bookings', { defaultValue: [] });
};

export const getMediaBooking = async (apiFetch, id) => {
    const response = await apiFetch(`/api/Marketing/MediaBookings/${id}`);
    return handleApiResponse(response, 'Failed to fetch media booking');
};

export const createMediaBooking = async (apiFetch, bookingData) => {
    const response = await apiFetch('/api/Marketing/MediaBookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
    });
    return handleApiResponse(response, 'Failed to create media booking');
};

export const updateMediaBooking = async (apiFetch, id, bookingData) => {
    const response = await apiFetch(`/api/Marketing/MediaBookings/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
    });
    return handleApiResponse(response, 'Failed to update media booking');
};

export const deleteMediaBooking = async (apiFetch, id) => {
    const response = await apiFetch(`/api/Marketing/MediaBookings/${id}`, {
        method: 'DELETE'
    });
    return handleApiResponse(response, 'Failed to delete media booking', { returnFullData: true });
};

export const getMediaBookingWeeklyView = async (apiFetch, startDate, endDate) => {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await apiFetch(`/api/Marketing/MediaBookings/weekly?${params}`);
    return handleApiResponse(response, 'Failed to fetch weekly media bookings', { defaultValue: [] });
};

// ============================================
// PROMOTIONAL BOOKINGS
// ============================================

export const getAllPromotionalBookings = async (apiFetch) => {
    const response = await apiFetch('/api/Marketing/PromotionalBookings');
    return handleApiResponse(response, 'Failed to fetch promotional bookings', { defaultValue: [] });
};

export const getPromotionalBooking = async (apiFetch, id) => {
    const response = await apiFetch(`/api/Marketing/PromotionalBookings/${id}`);
    return handleApiResponse(response, 'Failed to fetch promotional booking');
};

export const createPromotionalBooking = async (apiFetch, bookingData) => {
    const response = await apiFetch('/api/Marketing/PromotionalBookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
    });
    return handleApiResponse(response, 'Failed to create promotional booking');
};

export const updatePromotionalBooking = async (apiFetch, id, bookingData) => {
    const response = await apiFetch(`/api/Marketing/PromotionalBookings/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
    });
    return handleApiResponse(response, 'Failed to update promotional booking');
};

export const deletePromotionalBooking = async (apiFetch, id) => {
    const response = await apiFetch(`/api/Marketing/PromotionalBookings/${id}`, {
        method: 'DELETE'
    });
    return handleApiResponse(response, 'Failed to delete promotional booking', { returnFullData: true });
};

export const getPromotionalBookingWeeklyView = async (apiFetch, startDate, endDate) => {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await apiFetch(`/api/Marketing/PromotionalBookings/weekly?${params}`);
    return handleApiResponse(response, 'Failed to fetch weekly promotional bookings', { defaultValue: [] });
};

// ============================================
// NO CHARGE APPROVALS
// ============================================

export const getPendingApprovals = async (apiFetch) => {
    const response = await apiFetch('/api/Marketing/Approvals/pending');
    return handleApiResponse(response, 'Failed to fetch pending approvals', { defaultValue: [] });
};

/**
 * Validate and normalize booking type parameter to prevent path manipulation.
 * Only allows 'promotional' or 'media' booking types.
 */
const normalizeAndValidateBookingType = (bookingType) => {
    const normalized = String(bookingType).toLowerCase();
    const allowedTypes = ['promotional', 'media'];

    if (!allowedTypes.includes(normalized)) {
        throw new Error('Invalid booking type');
    }

    return normalized;
};

export const approveBooking = async (apiFetch, bookingId, bookingType) => {
    const type = normalizeAndValidateBookingType(bookingType);
    const safeBookingId = encodePathId(bookingId, 'bookingId');
    const response = await apiFetch(`/api/Marketing/Approvals/${type}/${safeBookingId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
    });
    return handleApiResponse(response, 'Failed to approve booking', { returnFullData: true });
};

export const rejectBooking = async (apiFetch, bookingId, bookingType, reason) => {
    const type = normalizeAndValidateBookingType(bookingType);
    const safeBookingId = encodePathId(bookingId, 'bookingId');
    const response = await apiFetch(`/api/Marketing/Approvals/${type}/${safeBookingId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
    });
    return handleApiResponse(response, 'Failed to reject booking', { returnFullData: true });
};

// ============================================
// PROPERTIES (Helper - uses existing Property controller)
// ============================================

export const getAllProperties = async (apiFetch) => {
    const response = await apiFetch('/api/Property/all');
    return handleApiResponse(response, 'Failed to fetch properties', { defaultValue: [] });
};
